import { useState, useEffect, useMemo } from 'react';
import { 
  Package, 
  AlertTriangle, 
  Users, 
  DollarSign, 
  RefreshCw,
  ShoppingCart,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Lock
} from 'lucide-react';
import { useProducts } from '../hooks/useProducts';
import { useClients } from '../hooks/useClients';
import { useCompras } from '../hooks/useCompras';
import { useDevoluciones } from '../hooks/useDevoluciones';
import { useSalesStats, useSalesMensuales, useTopProducts, useSalesDiarias } from '../hooks/useSales';
import { useCompraStats } from '../hooks/useCompras';
import { useGastosStats } from '../hooks/useGastos';
import { useGastosMensuales } from '../hooks/useGastos';
import { useGastosDistribucion } from '../hooks/useGastos';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { formatCurrency } from '../lib/utils';
import { SalesChart } from './Dashboard/components/SalesChart';
import { TopProducts } from './Dashboard/components/TopProducts';
import { ResumenFinanciero } from './Dashboard/components/ResumenFinanciero';
import { VentasVsGastos } from './Dashboard/components/VentasVsGastos';
import { DistribucionGastos } from './Dashboard/components/DistribucionGastos';
import { useDashboardVisibility } from '../hooks/useDashboardVisibility';

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const { isVisible } = useDashboardVisibility();

  // Estados para los periodos de los gráficos
  const [salesPeriod, setSalesPeriod] = useState<'7d' | '15d' | '30d' | '90d'>('7d');
  const [vgPeriod, setVgPeriod] = useState<6 | 12>(6);

  // Estadísticas generales
  const { data: salesStats, isLoading: salesStatsLoading } = useSalesStats();
  const { data: comprasStats, isLoading: comprasStatsLoading } = useCompraStats();
  const { data: gastosStats, isLoading: gastosStatsLoading } = useGastosStats();
  
  // Hooks para Ventas vs Gastos
  const { data: ventasMensuales = [], isLoading: ventasMensualesLoading } = useSalesMensuales(vgPeriod);
  const { data: gastosMensuales = [], isLoading: gastosMensualesLoading } = useGastosMensuales(vgPeriod);
  
  const { data: distribucionGastos = [], isLoading: distribucionGastosLoading } = useGastosDistribucion(1);
  const { data: topProducts = [], isLoading: topProductsLoading } = useTopProducts(10);

  // Hook para Ventas Diarias
  const diasVentas = parseInt(salesPeriod.replace('d', ''));
  const { data: ventasDiarias = [], isLoading: ventasDiariasLoading } = useSalesDiarias(diasVentas);

  const { data: rawProducts, isLoading: productsLoading } = useProducts();
  const { data: rawClients, isLoading: clientsLoading } = useClients();
  const { data: rawCompras, isLoading: comprasLoading } = useCompras();
  const { data: rawDevoluciones, isLoading: devolucionesLoading } = useDevoluciones();

  const products = Array.isArray(rawProducts as any) ? (rawProducts as any) : ((rawProducts as any)?.data || []);
  const totalProductos = (rawProducts as any)?.total || 0;
  
  const clients = Array.isArray(rawClients as any) ? (rawClients as any) : ((rawClients as any)?.data || []);
  const totalClientes = (rawClients as any)?.total || clients.length;
    
  const compras = Array.isArray(rawCompras as any) ? (rawCompras as any) : ((rawCompras as any)?.data || []);
  const devoluciones = Array.isArray(rawDevoluciones as any) ? (rawDevoluciones as any) : ((rawDevoluciones as any)?.data || []);

  const ventasVsGastosData = useMemo(() => {
    return ventasMensuales.map((item: any) => {
      const gasto = gastosMensuales.find((g: any) => g.mes === item.mes);
      return {
        mes: item.mes,
        ventas: Number(item.ventas) || 0,
        gastos: Number(gasto?.gastos) || 0,
      };
    });
  }, [ventasMensuales, gastosMensuales]);

  const salesChartData = useMemo(() => {
    return ventasDiarias.map((item: any) => {
      const dateObj = new Date(item.fecha);
      const fechaFormat = dateObj.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
      return {
        fecha: fechaFormat,
        ventas: Number(item.total) || 0,
        devoluciones: 0,
        ganancia: (Number(item.total) || 0) * 0.3,
      };
    });
  }, [ventasDiarias]);

  useEffect(() => {
    if (!productsLoading && !clientsLoading && !comprasLoading && !devolucionesLoading) {
      setLoading(false);
    }
  }, [productsLoading, clientsLoading, comprasLoading, devolucionesLoading]);

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando dashboard...</div>
      </div>
    );
  }

  const lowStockProducts = products.filter((p: any) => p.stock <= p.stockMinimo);
  const outOfStockProducts = products.filter((p: any) => p.stock === 0);
  const comprasPendientes = compras.filter((c: any) => c.estado === 'pendiente');
  const comprasRecibidas = compras.filter((c: any) => c.estado === 'recibida');
  const totalCompras = comprasRecibidas.reduce((sum: number, c: any) => sum + Number(c.total || 0), 0);
  const devolucionesPendientes = devoluciones.filter((d: any) => d.estado === 'pendiente');

  const ultimasCompras = compras.slice(0, 5);
  const ultimasDevoluciones = devoluciones.slice(0, 5);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-sm text-gray-500">Resumen general del sistema</p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-2 px-3 py-2 text-xs rounded-lg ${
            isVisible ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {isVisible ? (
              <>
                <Eye size={14} />
                Visible
              </>
            ) : (
              <>
                <Lock size={14} />
                Oculto
              </>
            )}
          </div>
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {isVisible ? (
        <>
          <ResumenFinanciero
            ventasHoy={salesStats?.totalHoy || 0}
            comprasHoy={comprasStats?.totalGasto || 0}
            gastosHoy={gastosStats?.totalHoy || 0}
            isLoading={salesStatsLoading || comprasStatsLoading || gastosStatsLoading}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Productos</p>
                  <p className="text-2xl font-bold text-gray-800">{totalProductos}</p>
                  <p className="text-xs text-gray-400">Total registrados</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Package size={24} className="text-blue-600" />
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Stock Bajo</p>
                  <p className="text-2xl font-bold text-red-600">{lowStockProducts.length}</p>
                  <p className="text-xs text-gray-400">
                    {outOfStockProducts.length} agotados
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertTriangle size={24} className="text-red-600" />
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Clientes</p>
                  <p className="text-2xl font-bold text-gray-800">{totalClientes}</p>
                  <p className="text-xs text-gray-400">Registrados</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Users size={24} className="text-green-600" />
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Compras</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(totalCompras)}
                  </p>
                  <p className="text-xs text-gray-400">
                    {comprasRecibidas.length} compras
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <DollarSign size={24} className="text-purple-600" />
                </div>
              </div>
            </Card>
          </div>

          {/* Gráfico 1: Ventas Diarias */}
          <SalesChart 
            key={`sales-${salesPeriod}`}
            data={salesChartData} 
            isLoading={ventasDiariasLoading}
            periodo={salesPeriod}
            onPeriodoChange={setSalesPeriod}
          />

          {/* Gráfico 2: Ventas vs Gastos */}
          <VentasVsGastos 
            key={`vg-${vgPeriod}`}
            data={ventasVsGastosData} 
            isLoading={ventasMensualesLoading || gastosMensualesLoading} 
            periodo={vgPeriod}
            onPeriodoChange={setVgPeriod}
          />

          <DistribucionGastos data={distribucionGastos} isLoading={distribucionGastosLoading} />

          <TopProducts products={topProducts as any} isLoading={topProductsLoading} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} className="text-red-500" />
                <h3 className="font-semibold text-gray-800">Stock Crítico</h3>
              </div>
              {outOfStockProducts.length > 0 ? (
                <ul className="space-y-1">
                  {outOfStockProducts.slice(0, 5).map((p: any) => (
                    <li key={p.id} className="text-sm text-gray-600 flex justify-between">
                      <span>{p.nombre}</span>
                      <span className="text-red-500 font-semibold">Agotado</span>
                    </li>
                  ))}
                  {outOfStockProducts.length > 5 && (
                    <li className="text-xs text-gray-400">
                      +{outOfStockProducts.length - 5} más
                    </li>
                  )}
                </ul>
              ) : (
                <p className="text-sm text-gray-500"> Todos tienen stock</p>
              )}
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <ShoppingCart size={18} className="text-orange-500" />
                <h3 className="font-semibold text-gray-800">Compras Pendientes</h3>
              </div>
              {comprasPendientes.length > 0 ? (
                <ul className="space-y-1">
                  {comprasPendientes.slice(0, 5).map((c: any) => (
                    <li key={c.id} className="text-sm text-gray-600 flex justify-between">
                      <span>{c.numeroCompra}</span>
                      <span className="text-orange-500">{c.proveedor?.nombre}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500"> No hay compras pendientes</p>
              )}
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText size={18} className="text-purple-500" />
                <h3 className="font-semibold text-gray-800">Devoluciones Pendientes</h3>
              </div>
              {devolucionesPendientes.length > 0 ? (
                <ul className="space-y-1">
                  {devolucionesPendientes.slice(0, 5).map((d: any) => (
                    <li key={d.id} className="text-sm text-gray-600 flex justify-between">
                      <span>{d.numeroDevolucion}</span>
                      <span className="text-purple-500">{d.clienteNombre}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500"> No hay devoluciones pendientes</p>
              )}
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <ShoppingCart size={18} className="text-blue-500" />
                  Últimas Compras
                </h3>
                <span className="text-xs text-gray-400">
                  {ultimasCompras.length} registros
                </span>
              </div>
              {ultimasCompras.length > 0 ? (
                <div className="space-y-2">
                  {ultimasCompras.map((c: any) => (
                    <div key={c.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
                      <div>
                        <p className="font-medium text-gray-800">{c.numeroCompra}</p>
                        <p className="text-xs text-gray-400">{c.proveedor?.nombre}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-800">{formatCurrency(c.total)}</p>
                        <span className="text-xs text-gray-400">
                          {c.estado === 'recibida' ? (
                            <CheckCircle size={12} className="text-green-500 inline" />
                          ) : c.estado === 'pendiente' ? (
                            <Clock size={12} className="text-yellow-500 inline" />
                          ) : (
                            <Clock size={12} className="text-blue-500 inline" />
                          )}
                          {' '}{c.estado}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No hay compras</p>
              )}
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <FileText size={18} className="text-purple-500" />
                  Últimas Devoluciones
                </h3>
                <span className="text-xs text-gray-400">
                  {ultimasDevoluciones.length} registros
                </span>
              </div>
              {ultimasDevoluciones.length > 0 ? (
                <div className="space-y-2">
                  {ultimasDevoluciones.map((d: any) => (
                    <div key={d.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
                      <div>
                        <p className="font-medium text-gray-800">{d.numeroDevolucion}</p>
                        <p className="text-xs text-gray-400">{d.clienteNombre}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-800">{formatCurrency(d.montoTotal)}</p>
                        <span className="text-xs text-gray-400">
                          {d.estado === 'aprobada' ? (
                            <CheckCircle size={12} className="text-green-500 inline" />
                          ) : d.estado === 'pendiente' ? (
                            <Clock size={12} className="text-yellow-500 inline" />
                          ) : (
                            <XCircle size={12} className="text-red-500 inline" />
                          )}
                          {' '}{d.estado}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No hay devoluciones</p>
              )}
            </Card>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="bg-gray-100 p-4 rounded-full mb-4">
            <Lock size={48} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-600">Dashboard oculto</h3>
          <p className="text-sm text-gray-400 mt-2 text-center max-w-md">
            Los KPIs, gráficos y métricas están ocultos por seguridad.
            <br />
            Puedes cambiar esta configuración en 
            <span className="font-medium text-blue-600"> Configuración → Seguridad</span>
          </p>
          <p className="text-xs text-gray-400 mt-4">
            Estado actual: <span className="font-medium">Oculto</span>
          </p>
        </div>
      )}
    </div>
  );
}